import { loginPathWithNext, safeNextPath, type Me } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Navigate, useLocation, useSearchParams } from "react-router";
import { api, ApiError } from "./api.js";
import { justLoggedOut, markLoggedOut, setLoggedIn } from "./lib/autoLogout.js";

export const ME_KEY = ["me"] as const;

/** ログイン中の自分。401 なら null（未ログイン） */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api<Me>("GET", "/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * ログインの情報（ME）を入れ替える（ログイン・確認のリンク・会社を選ぶ・ログアウトなど）。ME を直接 setQueryData しない＝必ずこれを通す。
 * 先に、いま飛んでいる GET /me を取り消す：入れ替える前に出た問い合わせ（ログイン前の 401＝null など）の答えがあとから届くと、
 * 新しいログインの情報を古い状態で上書きし、ホームでなくログインの画面に戻される（#176 の B1・#175 の help ⑤。画面の枠 AuthCard も useMe を呼ぶ）
 */
export async function setMe(qc: QueryClient, me: Me | null | unknown): Promise<void> {
  await qc.cancelQueries({ queryKey: ME_KEY });
  // 自動ログアウト（#189）：ログイン・会社の切替の時刻を覚える（それより前に出た要求の 401 を「切れた」と見ない）
  setLoggedIn(!!me, true);
  qc.setQueryData(ME_KEY, me);
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("POST", "/auth/logout"),
    onSettled: async () => {
      markLoggedOut(); // 自分でログアウトした＝ログインの画面に next を付けない（次にログインした人を前の画面に戻さない）
      await setMe(qc, null);
    },
  });
}

/**
 * ログインしていなければ、どこを開いてもログイン画面へ（docs/10 §9.2）。ログインのあとは開こうとした画面へ戻る（/login?next=…、#189）。
 * 取り直しが通信の一時的な失敗で断られても、前の ME があれば画面はそのまま描く（#189 A：「つながりません」の1行だけの画面にしない。
 * 画面の問い合わせはそれぞれの文を出し、つながったら戻る）
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const loc = useLocation();
  if (me.isPending) return <div className="auth"><p className="hint">読み込んでいます…</p></div>;
  if (me.isError && me.data === undefined) return <div className="auth"><p className="err">⚠ いまサーバーにつながりません。少し待ってから開き直してください。</p></div>;
  if (!me.data) return <Navigate to={justLoggedOut() ? "/login" : loginPathWithNext(loc.pathname + loc.search)} replace />;
  return <>{children}</>;
}

/** ログイン済みなら、ログイン・会員登録の画面は飛ばしてホーム（?next= があればその画面）へ */
export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const [sp] = useSearchParams();
  if (me.data) return <Navigate to={safeNextPath(sp.get("next")) ?? "/home"} replace />;
  return <>{children}</>;
}
